import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import IconChevronRight from "@/assets/icons/chevron-right.svg?react";
import IconClose from "@/assets/icons/close.svg?react";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer.tsx";
import { cn } from "@/lib/utils";
import { useGetP2pFAQs, P2pFAQ } from "../api";

function FAQItem({
  faq,
  open,
  onToggle,
}: {
  faq: P2pFAQ;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border overflow-hidden transition-colors duration-200",
        open
          ? "bg-orange-100/[0.06] border-orange-100/30"
          : "bg-white/[0.03] border-white/[0.06]"
      )}
    >
      <VibratingButton
        onClick={onToggle}
        vibrationType="selection"
        className="w-full p-4 flex items-center gap-3 text-left"
      >
        <span
          className={cn(
            "text-[15px] font-semibold leading-140 flex-1 transition-colors",
            open ? "text-orange-100" : "text-white"
          )}
        >
          {faq.question}
        </span>
        <motion.span
          animate={{ rotate: open ? 90 : 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className={cn(
            "shrink-0 transition-colors",
            open ? "text-orange-100" : "text-gray-1"
          )}
        >
          <IconChevronRight className="size-5" />
        </motion.span>
      </VibratingButton>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div
              className="px-4 pb-4 text-sm text-white/85 leading-160 [&_a]:text-orange-100 [&_a]:underline [&_p]:mb-2 last:[&_p]:mb-0"
              dangerouslySetInnerHTML={{ __html: faq.answer }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface P2pFAQSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function P2pFAQSheet({ isOpen, onClose }: P2pFAQSheetProps) {
  const { t } = useTranslation();
  const { data: faqs, isLoading } = useGetP2pFAQs();
  // Accordion — only one item open at a time. Reset when the sheet
  // closes so re-opening starts from a collapsed state.
  const [openId, setOpenId] = useState<string | null>(null);
  useEffect(() => {
    if (!isOpen) setOpenId(null);
  }, [isOpen]);

  return (
    <Drawer open={isOpen} onOpenChange={(e) => !e && onClose()}>
      <DrawerContent className="bg-gray-4 max-h-[85vh]" showTopThumb>
        <DrawerHeader className="flex justify-between items-center mb-3">
          <DrawerTitle className="text-lg">
            {t("p2p_faq_title") || "Savol-javoblar"}
          </DrawerTitle>
          <DrawerClose>
            <IconClose className="size-6 text-gray-1 cursor-pointer" />
          </DrawerClose>
        </DrawerHeader>
        <DrawerBody className="overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col gap-3 pb-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl bg-white/[0.03] border border-white/[0.06] h-[64px] skeleton"
                />
              ))}
            </div>
          ) : !faqs?.length ? (
            <p className="text-sm text-white/60 text-center py-8">
              {t("p2p_faq_empty") || "Hozircha savol-javoblar yo'q"}
            </p>
          ) : (
            <div className="flex flex-col gap-3 pb-4">
              {faqs.map((faq) => (
                <FAQItem
                  key={faq.id}
                  faq={faq}
                  open={openId === faq.id}
                  onToggle={() =>
                    setOpenId((prev) => (prev === faq.id ? null : faq.id))
                  }
                />
              ))}
            </div>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
